import Raven from 'raven-js';
import * as React from 'react';
import ReactDOM from 'react-dom';

const pingedCallsites = new Set<string>();

const resolveNode = (
  instance: React.ReactInstance | null | undefined
): Element | Text | null => {
  const domInternals = (ReactDOM as any)
    .__DOM_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE;
  if (domInternals?.findDOMNode) {
    // React 19's internal findDOMNode doesn't handle null/Element inputs — it accesses
    // instance._reactInternals directly. React 18 handles these cases in findDOMNode itself:
    // https://github.com/facebook/react/blob/f1338f8080abd1386454a10bbf93d67bfe37ce85/packages/react-dom/src/client/ReactDOMLegacy.js#L273-L278
    if (instance == null) {
      return null;
      // https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType#node.element_node
    } else if (
      'nodeType' in instance &&
      instance.nodeType === 1 /* ELEMENT_NODE */
    ) {
      return instance;
    }
    // eslint-disable-next-line react/no-find-dom-node
    return domInternals.findDOMNode(instance);
  }
  // eslint-disable-next-line react/no-find-dom-node
  return (ReactDOM as any).findDOMNode(instance);
};

// React 19 moves findDOMNode to an internal object that suppresses Strict Mode warnings.
// React 18 doesn't have that object, so we fall back to ReactDOM.findDOMNode.
// This must be a function (not a module-level const) because React 19 initializes
// __DOM_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE.findDOMNode as null
// at module load time — the actual function is injected later by the renderer.
export const findDOMNode = (
  instance: React.ReactInstance | null | undefined,
  callsite?: string
): Element | Text | null => {
  const result = resolveNode(instance);
  if (callsite && !pingedCallsites.has(callsite)) {
    pingedCallsites.add(callsite);
    Raven.capturePageEvent('findDOMNode-usage', {
      extra: {
        callsite,
        pathname: window.location.pathname,
        reactVersion: React.version,
        resolvedTagName: result?.nodeName || 'null',
      },
    });
  }
  return result;
};
